import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { BankAccountService } from '../../../../../modules/finanzas/bancos/bankaccount.service';
import {
  BankAccountModel,
  MovimientoBancoBankAccounts,
  MovimientoEfectivoBankAccounts
} from '../../../../../modules/finanzas/bancos/bankaccount.model';
import { BankAccountsDialogMovimientoBancoComponent } from '../../../../../modules/finanzas/bancos/bankaccount-dialog-movimiento-banco.component';
import { BankAccountsDialogMovimientoEfectivoComponent } from '../../../../../modules/finanzas/bancos/bankaccount-dialog-movimiento-efectivo.component';
import { Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

interface BankAccountMovementLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
}

interface BankAccountMovementBlock {
  kind: 'banco' | 'efectivo';
  type: number;
  title: string;
  icon: string;
  singular: string;
  total: number;
  count: number;
  lines: BankAccountMovementLine[];
}

const MAX_LINES = 5;

@Component({
  selector: 'app-bank-account-detail',
  templateUrl: './bank-account-detail.component.html',
  styleUrls: ['./bank-account-detail.component.scss'],
  standalone: false,
})
export class BankAccountDetailComponent implements OnChanges {
  @Input() row!: BankAccountModel;
  @Output() changed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<BankAccountModel>();

  readonly maxLines = MAX_LINES;

  loading = true;
  error = false;
  blocks: BankAccountMovementBlock[] = [];

  private loadedUid = '';

  constructor(
    private bankAccountService: BankAccountService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get formattedIban(): string {
    const clean = ((this.row && this.row.iban) || '').replace(/\s+/g, '');
    return clean.replace(/(.{4})(?=.)/g, '$1 ');
  }

  ngOnChanges(): void {
    if (!this.row) return;
    if (!this.row.uid) {
      this.loading = false;
      this.blocks = [];
      return;
    }
    if (this.row.uid === this.loadedUid) return;
    this.loadedUid = this.row.uid;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(block: BankAccountMovementBlock): void {
    this.openDialog(block);
  }

  manage(block: BankAccountMovementBlock): void {
    this.openDialog(block);
  }

  editLine(block: BankAccountMovementBlock, line: BankAccountMovementLine): void {
    this.openDialog(block, line.uid);
  }

  deleteLine(block: BankAccountMovementBlock, line: BankAccountMovementLine): void {
    const action = block.kind === 'banco'
      ? () => this.bankAccountService.movimientoBancoBankAccountsDelete(line.uid)
      : () => this.bankAccountService.movimientoEfectivoBankAccountsDelete(line.uid);

    this.confirm.open({
      title: `Eliminar ${block.singular}`,
      subtitle: [this.row.bank, this.formattedIban].filter(Boolean).join(' · ') || undefined,
      message: 'Se eliminará el movimiento y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando movimiento…',
      errorText: 'Ha ocurrido un error al eliminar el movimiento',
      action,
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Movimiento eliminado correctamente');
      this.refresh();
    });
  }

  trackByUid(_: number, line: BankAccountMovementLine): string {
    return line.uid;
  }

  trackByBlock(_: number, block: BankAccountMovementBlock): string {
    return block.kind + block.type;
  }

  private openDialog(block: BankAccountMovementBlock, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    const dialog$ = block.kind === 'banco'
      ? BankAccountsDialogMovimientoBancoComponent.open(this.dialog, { bankAccount: this.row, tipoMovimientoBancoId: block.type, editUid })
      : BankAccountsDialogMovimientoEfectivoComponent.open(this.dialog, { bankAccount: this.row, tipoMovimientoEfectivoId: block.type, editUid });
    dialog$.subscribe(changed => { if (changed) this.refresh(); });
  }

  private refresh(): void {
    this.changed.emit();
  }

  private load(uid: string): void {
    this.loading = true;
    this.error = false;

    Promise.all([
      this.bankAccountService.movimientoBancoBankAccountsList(uid, 1),
      this.bankAccountService.movimientoEfectivoBankAccountsList(uid, 1),
      this.bankAccountService.movimientoEfectivoBankAccountsList(uid, 2),
    ])
      .then(([transfers, withdrawals, deposits]) => {
        if (!this.row || uid !== this.row.uid) return;
        this.blocks = [
          this.buildBlock('banco', 1, 'Traspasos', 'swap_horiz', 'traspaso', this.bankLines(transfers)),
          this.buildBlock('efectivo', 1, 'Reintegros de efectivo', 'local_atm', 'reintegro', this.cashLines(withdrawals)),
          this.buildBlock('efectivo', 2, 'Ingresos de efectivo', 'account_balance_wallet', 'ingreso', this.cashLines(deposits)),
        ];
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private bankLines(list: MovimientoBancoBankAccounts[]): BankAccountMovementLine[] {
    return (Array.isArray(list) ? list : []).map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: item.concepto || 'Sin concepto',
      meta: [item.destinoAccountInfo ? 'Destino ' + item.destinoAccountInfo : '', item.activitiesName || ''].filter(Boolean).join(' · '),
      amount: Number(item.importe) || 0,
    }));
  }

  private cashLines(list: MovimientoEfectivoBankAccounts[]): BankAccountMovementLine[] {
    return (Array.isArray(list) ? list : []).map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: item.concepto || 'Sin concepto',
      meta: [item.efectivoName || '', item.nombreActividad || ''].filter(Boolean).join(' · '),
      amount: Number(item.importe) || 0,
    }));
  }

  private buildBlock(kind: 'banco' | 'efectivo', type: number, title: string, icon: string, singular: string, lines: BankAccountMovementLine[]): BankAccountMovementBlock {
    const total = Utils.RoundTwoDecimals(lines.reduce((acc, line) => acc + line.amount, 0));
    return { kind, type, title, icon, singular, total, count: lines.length, lines: lines.slice(0, MAX_LINES) };
  }

  private details(line: BankAccountMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Concepto', value: line.concept });
    if (line.meta) details.push({ label: 'Detalle', value: line.meta });
    details.push({ label: 'Importe', value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }
}
